import React from 'react'
import { useAppSelector } from '@/store/hooks'
import { Wifi, WifiOff, Database, RefreshCw } from 'lucide-react'

interface OpsConnectionStatusProps {
  onReconnect?: () => void
  className?: string
}

export const OpsConnectionStatus: React.FC<OpsConnectionStatusProps> = ({
  onReconnect,
  className = ''
}) => {
  const { connection } = useAppSelector(state => state.socket)
  const { connected: isConnected, connecting, reconnecting, error } = connection

  // Don't show if everything is connected and working
  if (isConnected && !error) {
    return null
  }

  let statusContent
  let statusColor = 'text-muted-foreground'
  let bgColor = 'bg-muted/50'

  if (connecting || reconnecting) {
    statusColor = 'text-yellow-600 dark:text-yellow-400'
    bgColor = 'bg-yellow-50 dark:bg-yellow-900/20'
    statusContent = (
      <div className="flex items-center space-x-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="text-sm font-medium">
          {reconnecting ? 'Reconnecting to PowerBI...' : 'Connecting to PowerBI...'}
        </span>
      </div>
    )
  } else if (!isConnected) {
    statusColor = 'text-destructive'
    bgColor = 'bg-destructive/10'
    statusContent = (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-2">
          <WifiOff className="w-4 h-4" />
          <div>
            <div className="text-sm font-medium">PowerBI Connection Lost</div>
            <div className="text-xs opacity-75">
              Real-time data updates unavailable. You can still ask questions.
            </div>
          </div>
        </div>
        {onReconnect && (
          <button
            onClick={onReconnect}
            className="ml-4 px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Reconnect
          </button>
        )}
      </div>
    )
  } else {
    // Connected but with error
    statusColor = 'text-orange-600 dark:text-orange-400'
    bgColor = 'bg-orange-50 dark:bg-orange-900/20'
    statusContent = (
      <div className="flex items-center space-x-2">
        <Database className="w-4 h-4" />
        <div>
          <div className="text-sm font-medium">PowerBI Connected with Issues</div>
          <div className="text-xs opacity-75">
            Some features may be limited. {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`rounded-lg border p-3 ${bgColor} ${statusColor} ${className}`}>
      {statusContent}
    </div>
  )
}

export const OpsTypingIndicator: React.FC<{ typingUsers: string[] }> = ({ typingUsers }) => {
  if (typingUsers.length === 0) return null

  return (
    <div className="flex items-start space-x-4">
      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
        <Database className="w-5 h-5 text-primary" />
      </div>
      <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-sm text-muted-foreground">
            PowerBI is processing your query...
          </span>
        </div>
      </div>
    </div>
  )
}

export default OpsConnectionStatus